import * as THREE from "three";
import { speedSteerRate, vehicleSubsteps, type VehicleDef, type VehicleHardpointDef, type VehicleSeatDef } from "@hyperlight/shared";
import type { Input } from "../engine/input";

/** Downward spring stiffness (fraction of the probe's own spring) used to slam
 * the hull onto the ground when a drift starts, so the drop is crisp instead of
 * a slow gravity fall. Self-limiting: zero once the probe reaches the target. */
const DRIFT_SLAM_K = 5.0;

export interface VehicleGroundSample {
  point: THREE.Vector3;
  normal: THREE.Vector3;
}

/** Runtime adapter over analytic terrain/collision. The harness supplies a
 * plane; gameplay supplies a rotating Planet without coupling the controller. */
export interface VehicleGround {
  /** False only while this host is atomically loading its finite terrain solids. */
  readonly ready?: boolean;
  center: THREE.Vector3;
  sample(worldPoint: THREE.Vector3, out: VehicleGroundSample): boolean;
  /** Local air density as a FRACTION of sea level (1 at the surface, 0 above
   * the atmosphere, 0 everywhere on an airless world). Every aerodynamic and
   * flight-thrust term is multiplied by it, which is where the soft flight
   * ceiling comes from: climb and the wing and the engine fade out together —
   * no wall, no clamp. Omit to mean "always 1" (the harness plane). */
  airDensityAt?(worldPoint: THREE.Vector3): number;
  sweep?(from: THREE.Vector3, to: THREE.Vector3, radius: number): { t: number; normal: THREE.Vector3 } | null;
  /** Resolve streamed props such as tree trunks after each bounded substep. */
  resolveObstacle?(position: THREE.Vector3, radius: number, out: THREE.Vector3): boolean;
}

const WORLD_UP = new THREE.Vector3(0, 1, 0);
const CAMERA_REST_PITCH = 0.22;
/** Seconds of zero probe contact before a FLYER engages flight on its own —
 * long enough that taxi bumps stay bumps, short enough that driving off a
 * ridge feels like taking wing rather than falling. */
const FLIGHT_AUTO_ENGAGE_T = 0.35;
/** Inward collision speed required to publish sparks/audio/shake. Physics still
 * resolves softer contacts; they simply remain presentation-silent. */
const PRESENTATION_IMPACT_MIN = 24;
const GLIDE_LANDING_ARM_TIME = 0.4;

export class GroundVehicle {
  readonly pos = new THREE.Vector3();
  readonly prevPos = new THREE.Vector3();
  readonly quat = new THREE.Quaternion();
  readonly prevQuat = new THREE.Quaternion();
  readonly vel = new THREE.Vector3();
  readonly angularVel = new THREE.Vector3();
  readonly viewHeading = new THREE.Vector3(0, 0, -1);
  /** Full camera aim including pitch; ground steering uses its tangent projection. */
  readonly aimHeading = new THREE.Vector3(0, 0, -1);
  readonly up = new THREE.Vector3(0, 1, 0);
  boostEnergy: number;
  boostK = 0;
  /** Set for one frame when boost engages (see the assignment for why). */
  boostStarted = false;
  driftK = 0;
  /** Separate, faster-easing drift factor that ONLY drives the ride-height drop,
   * so the hull snaps down/up quicker than the handling drift (driftK) eases. */
  driftDropK = 0;
  /** Space detaches the suspension and deploys the vehicle's lifting surfaces.
   * Touchdown automatically returns to ground mode. */
  gliding = false;
  glideTime = 0;
  /** Local air-density fraction sampled each substep. Presentation reads it for
   * the near-ceiling engine sputter; 1 wherever the adapter has no atmosphere
   * model. */
  airDensity = 1;
  /** Hull height above the terrain directly below, metres — the camera fades
   * the environment speed-warp on it, because that effect is a GROUND RUSH cue
   * and bending the horizon (and the sea) around a high-altitude cruise is
   * exactly the reported "world curves around the player" artifact. */
  groundClearance = 0;
  /** Seconds since the last probe contact; drives a flyer's auto-engage. */
  airborneT = 0;
  airspeed = 0;
  angleOfAttack = 0;
  stalled = false;
  /** Hover-system power: empty vehicles settle onto the hard chassis floor;
   * reserving or occupying a seat restores their authored ride height. */
  hoverK: number;
  groundedFraction = 0;
  forwardSpeed = 0;
  cameraPitch = CAMERA_REST_PITCH;
  occupiedSeatId: string | null = null;

  private boostRegenT = 0;
  private probeWorld = new THREE.Vector3();
  private probeOffset = new THREE.Vector3();
  private pointVel = new THREE.Vector3();
  private force = new THREE.Vector3();
  private groundNormal = new THREE.Vector3();
  private bodyFwd = new THREE.Vector3();
  private bodyUp = new THREE.Vector3();
  private bodyRight = new THREE.Vector3();
  private desiredFwd = new THREE.Vector3();
  private tangentVel = new THREE.Vector3();
  private from = new THREE.Vector3();
  private axis = new THREE.Vector3();
  private dq = new THREE.Quaternion();
  private sampleOut: VehicleGroundSample = { point: new THREE.Vector3(), normal: new THREE.Vector3() };
  private yawPart = new THREE.Vector3();
  private alignedFwd = new THREE.Vector3();
  private alignedRight = new THREE.Vector3();
  private alignmentMatrix = new THREE.Matrix4();
  private alignmentQuat = new THREE.Quaternion();
  private terrainCorrection = new THREE.Vector3();
  private terrainHitNormal = new THREE.Vector3();
  private airRight = new THREE.Vector3();
  private airUp = new THREE.Vector3();
  private airMatrix = new THREE.Matrix4();
  private airQuat = new THREE.Quaternion();
  private obstacleCorrection = new THREE.Vector3();
  private obstacleNormal = new THREE.Vector3();
  private recoveryVelocity = new THREE.Vector3();
  private flowDir = new THREE.Vector3();
  private liftDir = new THREE.Vector3();
  private pendingImpact = 0;
  private reservedSeatId: string | null = null;
  private impactPoint = new THREE.Vector3();
  private impactNormal = new THREE.Vector3();

  constructor(readonly def: VehicleDef) {
    this.boostEnergy = def.boost?.capacity ?? 0;
    this.hoverK = def.hover ? 0 : 1;
  }

  get occupied(): boolean { return this.occupiedSeatId !== null; }

  /** A boarding driver can power and control the chassis before the animation's
   * final occupancy handoff. */
  get powered(): boolean { return this.occupied || this.reservedSeatId !== null; }

  get reservedSeat(): VehicleSeatDef | null {
    return this.reservedSeatId === null
      ? null
      : this.def.seats.find((seat) => seat.id === this.reservedSeatId) ?? null;
  }

  get activeSeat(): VehicleSeatDef | null {
    return this.occupiedSeatId === null
      ? null
      : this.def.seats.find((seat) => seat.id === this.occupiedSeatId) ?? null;
  }

  /** Claim a concrete seat. The default is the driver seat used by local gameplay. */
  occupySeat(seatId?: string): VehicleSeatDef | null {
    const seat = seatId
      ? this.def.seats.find((candidate) => candidate.id === seatId)
      : this.def.seats.find((candidate) => candidate.role === "driver");
    if (!seat) return null;
    this.occupiedSeatId = seat.id;
    this.reservedSeatId = null;
    return seat;
  }

  /** Reserve during an enter animation. This powers the local driver controls;
   * occupancy still transfers only at the authored attach marker. */
  reserveSeat(seatId?: string): VehicleSeatDef | null {
    if (this.occupiedSeatId !== null || this.reservedSeatId !== null) return null;
    const seat = seatId
      ? this.def.seats.find((candidate) => candidate.id === seatId)
      : this.def.seats.find((candidate) => candidate.role === "driver");
    if (!seat) return null;
    this.reservedSeatId = seat.id;
    return seat;
  }

  occupyReservedSeat(): VehicleSeatDef | null {
    return this.reservedSeatId ? this.occupySeat(this.reservedSeatId) : null;
  }

  vacateSeat(): void { this.occupiedSeatId = null; this.reservedSeatId = null; }

  /** Re-centre the chase/aim frame on the physical chassis. Dismount uses this
   * so camera-directed steering cannot leave the view yawed away from the
   * rider body's forward axis. */
  alignViewToChassis(resetPitch = true): void {
    this.viewHeading.set(0, 0, -1).applyQuaternion(this.quat)
      .addScaledVector(this.up, -this.viewHeading.dot(this.up));
    if (this.viewHeading.lengthSq() < 1e-6) this.viewHeading.copy(this.desiredFwd);
    this.viewHeading.normalize();
    this.aimHeading.copy(this.viewHeading);
    if (resetPitch) this.cameraPitch = CAMERA_REST_PITCH;
  }

  /** Drain the strongest collision accumulated since the previous render.
   * Fixed simulation may run several steps per frame, so an accumulator keeps
   * short contacts from being missed by presentation systems. */
  drainImpact(outPoint: THREE.Vector3, outNormal: THREE.Vector3): number {
    const strength = this.pendingImpact;
    if (strength <= 0) return 0;
    outPoint.copy(this.impactPoint);
    outNormal.copy(this.impactNormal);
    this.pendingImpact = 0;
    return strength;
  }

  /** Resolve an authored socket into simulation-world space. Rendering and
   * firing can share this without depending on the procedural shell. */
  hardpointPose(id: string, outPosition: THREE.Vector3, outDirection: THREE.Vector3): VehicleHardpointDef | null {
    const hardpoint = this.def.hardpoints.find((candidate) => candidate.id === id);
    if (!hardpoint) return null;
    outPosition.set(...hardpoint.position).applyQuaternion(this.quat).add(this.pos);
    outDirection.set(...hardpoint.direction).applyQuaternion(this.quat).normalize();
    return hardpoint;
  }

  spawn(position: THREE.Vector3, forward: THREE.Vector3, up: THREE.Vector3): void {
    this.vacateSeat();
    this.pos.copy(position);
    this.up.copy(up).normalize();
    this.desiredFwd.copy(forward).addScaledVector(this.up, -forward.dot(this.up)).normalize();
    if (this.desiredFwd.lengthSq() < 0.5) this.desiredFwd.set(0, 0, -1);
    this.quat.setFromRotationMatrix(new THREE.Matrix4().makeBasis(
      this.bodyRight.crossVectors(this.desiredFwd, this.up).normalize(),
      this.up,
      this.bodyFwd.copy(this.desiredFwd).negate(),
    ));
    this.viewHeading.copy(this.desiredFwd);
    this.aimHeading.copy(this.desiredFwd);
    this.cameraPitch = CAMERA_REST_PITCH;
    this.vel.set(0, 0, 0);
    this.angularVel.set(0, 0, 0);
    this.gliding = false;
    this.glideTime = 0;
    this.airspeed = 0;
    this.angleOfAttack = 0;
    this.stalled = false;
    this.hoverK = this.def.hover ? (this.powered ? 1 : 0) : 1;
    this.snapVisual();
  }

  snapVisual(): void {
    this.prevPos.copy(this.pos);
    this.prevQuat.copy(this.quat);
  }

  /** Follow an externally moved rotating frame without injecting its orbital
   * velocity into the vehicle's ground-relative handling. */
  carry(deltaPos: THREE.Vector3, deltaQuat: THREE.Quaternion): void {
    this.pos.applyQuaternion(deltaQuat).add(deltaPos);
    this.vel.applyQuaternion(deltaQuat);
    this.quat.premultiply(deltaQuat).normalize();
    this.viewHeading.applyQuaternion(deltaQuat).normalize();
    this.aimHeading.applyQuaternion(deltaQuat).normalize();
    this.angularVel.applyQuaternion(deltaQuat);
  }

  update(dt: number, input: Input, ground: VehicleGround): void {
    if (ground.ready === false) return;
    const { dx, dy } = input.consumeMouse();
    this.up.copy(this.pos).sub(ground.center).normalize();
    if (this.up.lengthSq() < 0.5) this.up.copy(WORLD_UP);
    if (dx !== 0) this.viewHeading.applyAxisAngle(this.up, -dx * 0.0019);
    this.viewHeading.addScaledVector(this.up, -this.viewHeading.dot(this.up)).normalize();
    this.cameraPitch = Math.max(-0.45, Math.min(1.05,
      this.cameraPitch + dy * 0.0015 * (input.invertY ? -1 : 1),
    ));
    // The chase boom's neutral pitch is 0.22. Measure aim relative to that
    // neutral so releasing the mouse holds a level chassis in midair.
    const aimPitch = Math.max(-0.7, Math.min(0.7, this.cameraPitch - 0.22));
    this.aimHeading.copy(this.viewHeading).multiplyScalar(Math.cos(aimPitch));
    this.aimHeading.addScaledVector(this.up, -Math.sin(aimPitch)).normalize();

    const throttle = (input.isDown("KeyW") ? 1 : 0) - (input.isDown("KeyS") ? 1 : 0);
    const steer = (input.isDown("KeyA") ? 1 : 0) - (input.isDown("KeyD") ? 1 : 0);
    if (this.def.flight) {
      // A flyer has NO detach gesture: Space is simply "up". Holding it on the
      // ground lifts off, and running out of ground (a ridge, a cliff lip)
      // engages flight by itself after a grace period — the grace is what keeps
      // ordinary taxi bumps from flickering the hover system off. X is down,
      // and descending onto the probes lands as it always did.
      if (!this.gliding && (input.isDown("Space") || this.airborneT > FLIGHT_AUTO_ENGAGE_T)) {
        this.detachFromGround();
      }
    } else if (input.wasPressed("Space") && !this.gliding) {
      this.detachFromGround();
    }
    // Climb/sink trim for powered flyers, in the same vocabulary the character
    // uses on foot (Space up, X down).
    const climb = this.def.flight
      ? (input.isDown("Space") ? 1 : 0) - (input.isDown("KeyX") ? 1 : 0)
      : 0;
    const handbrake = input.isButtonDown(2);
    const wantsBoost = throttle > 0 && (input.isDown("ShiftLeft") || input.isDown("ShiftRight"));
    const speed = this.vel.length();
    const steps = vehicleSubsteps(speed, this.def.collider.radius, dt);
    for (let i = 0; i < steps; i++) this.step(dt / steps, throttle, steer, climb, handbrake, wantsBoost, ground, true);
  }

  coast(dt: number, ground: VehicleGround, braking = true): void {
    if (ground.ready === false) return;
    const steps = vehicleSubsteps(this.vel.length(), this.def.collider.radius, dt);
    for (let i = 0; i < steps; i++) this.step(dt / steps, 0, 0, 0, braking, false, ground, false);
  }

  private step(
    dt: number,
    throttle: number,
    steer: number,
    climb: number,
    handbrake: boolean,
    wantsBoost: boolean,
    ground: VehicleGround,
    allowDrift: boolean,
  ): void {
    const h = this.def.handling;
    const boost = this.def.boost;
    const hover = this.def.hover;
    if (hover) {
      const response = this.powered ? hover.liftResponse : hover.settleResponse;
      this.hoverK += ((this.powered ? 1 : 0) - this.hoverK) * (1 - Math.exp(-response * dt));
    } else {
      this.hoverK = 1;
    }
    if (this.gliding) this.glideTime += dt;
    const boosting = !!boost && wantsBoost && this.boostEnergy > 0;
    if (boosting && boost) {
      this.boostEnergy = Math.max(0, this.boostEnergy - boost.burnPerSecond * dt);
      this.boostRegenT = boost.regenDelay;
    } else if (boost) {
      this.boostRegenT = Math.max(0, this.boostRegenT - dt);
      if (this.boostRegenT === 0) this.boostEnergy = Math.min(boost.capacity, this.boostEnergy + boost.regenPerSecond * dt);
    }
    /** True only on the frame boost ENGAGES — the camera's FOV punch keys off
     * the press rather than the level, so it hits once instead of every frame. */
    this.boostStarted = boosting && this.boostK < 0.02;
    this.boostK += ((boosting ? 1 : 0) - this.boostK) * (1 - Math.exp(-9 * dt));

    this.bodyFwd.set(0, 0, -1).applyQuaternion(this.quat).normalize();
    this.bodyUp.set(0, 1, 0).applyQuaternion(this.quat).normalize();
    this.bodyRight.set(1, 0, 0).applyQuaternion(this.quat).normalize();
    this.groundNormal.set(0, 0, 0);
    let contacts = 0;
    let landingContact = false;
    const walkableCos = Math.cos(this.def.handling.maxSlopeDeg * Math.PI / 180);

    for (const probe of this.def.probes) {
      this.probeOffset.set(...probe.position).applyQuaternion(this.quat);
      this.probeWorld.copy(this.pos).add(this.probeOffset);
      if (!ground.sample(this.probeWorld, this.sampleOut)) continue;
      if (this.sampleOut.normal.dot(this.up) < walkableCos) continue;
      const clearance = this.pointVel.copy(this.probeWorld).sub(this.sampleOut.point).dot(this.sampleOut.normal);
      if (clearance > probe.rideHeight + probe.travel) continue;
      contacts++;
      this.groundNormal.add(this.sampleOut.normal);
      const hullFloor = Math.max(0.08, this.def.collider.halfHeight + probe.position[1]);
      if (clearance <= hullFloor + 0.18) landingContact = true;
      // Detached lifting surfaces own vertical motion. Keep sampling so a
      // descending hull can recognize touchdown, but do not let suspension
      // create lift or magnetically reacquire the terrain in flight.
      if (this.gliding) continue;
      this.pointVel.copy(this.angularVel).cross(this.probeOffset).add(this.vel);
      const normalSpeed = this.pointVel.dot(this.sampleOut.normal);
      // Rest height includes the static sag needed to carry this probe's
      // share of the chassis. Unlike a constant preload, this still lets a
      // vehicle fall back down when it starts above its authored ride height.
      const supportSag = (this.def.mass * h.gravity / this.def.probes.length) / probe.spring;
      // Handbrake drift drags the hull along the ground: the ride-height target
      // collapses toward 0 as the slide builds (driftK 0→1), so the chassis
      // sinks from its hover onto the surface. At equilibrium resting clearance
      // equals this target, so a target of 0 means the belly skims the terrain.
      const poweredRide = hover
        ? THREE.MathUtils.lerp(hover.parkedRideHeight, probe.rideHeight, this.hoverK)
        : probe.rideHeight;
      const driftRide = poweredRide * (1 - this.driftDropK);
      // Active slam-down: while the hull still hovers above the collapsed drift
      // target, pull it down (spring-damped) so it reaches the ground crisply
      // rather than free-falling under gravity. Fades to zero at the target, so
      // it does not fight the normal up-spring once the belly is down.
      if (this.driftDropK > 0.01 && clearance > driftRide) {
        const slamUp = -(clearance - driftRide) * probe.spring * DRIFT_SLAM_K - normalSpeed * probe.damping;
        this.vel.addScaledVector(this.sampleOut.normal, this.driftDropK * slamUp * dt / this.def.mass);
      }
      const compression = Math.max(0, driftRide + supportSag - clearance);
      const magnitude = compression * probe.spring - normalSpeed * probe.damping;
      if (magnitude <= 0) continue;
      this.force.copy(this.sampleOut.normal).multiplyScalar(magnitude);
      this.vel.addScaledVector(this.force, dt / this.def.mass);
    }

    if (this.gliding && this.glideTime >= GLIDE_LANDING_ARM_TIME && landingContact && this.vel.dot(this.up) <= 0) {
      this.gliding = false;
      this.glideTime = 0;
      this.stalled = false;
    }
    this.groundedFraction = this.gliding ? 0 : contacts / Math.max(1, this.def.probes.length);
    if (contacts > 0) this.groundNormal.normalize();
    else this.groundNormal.copy(this.up);
    this.airborneT = contacts > 0 ? 0 : this.airborneT + dt;
    // Hull clearance from the centre sample — cheap (one extra sample), and the
    // only altitude signal the presentation layer has.
    this.groundClearance = ground.sample(this.pos, this.sampleOut)
      ? this.pointVel.copy(this.pos).sub(this.sampleOut.point).dot(this.sampleOut.normal)
      : 1000;

    // Full suspension support uses the authored baseline. As probes leave a
    // ramp, gravity blends toward the stronger airborne value so launches
    // retain momentum without hanging weightlessly for several seconds.
    const airK = 1 - this.groundedFraction;
    // The stronger legacy airborne gravity keeps ordinary ramp jumps crisp.
    // A deployed glider instead uses authored local gravity and earns its
    // descent rate from lift/drag, rather than an arcade gravity multiplier.
    const gravity = h.gravity * (this.gliding ? 1 : (1 + (h.airborneGravityMultiplier - 1) * airK));
    this.vel.addScaledVector(this.up, -gravity * dt);
    this.airDensity = ground.airDensityAt?.(this.pos) ?? 1;
    if (this.gliding) this.applyGlideAerodynamics(dt);
    else {
      this.airspeed = 0;
      this.angleOfAttack = 0;
      this.stalled = false;
    }
    const flight = this.def.flight;
    if (this.gliding && flight) {
      // Powered flight: sustained nose thrust, VTOL weight offset, climb trim.
      // Every term is scaled by the local density fraction — the soft ceiling
      // IS this scaling, and on an airless world the block does nothing at all.
      const rho = this.airDensity;
      if (throttle !== 0) {
        // Reverse throttle is retro-thrust at reduced authority: enough to
        // brake for a landing, not enough to fly backwards well.
        const authority = throttle > 0 ? 1 : 0.4;
        this.vel.addScaledVector(this.bodyFwd, flight.thrust * authority * throttle * rho * dt / this.def.mass);
      }
      // vtolLift carries this fraction of weight with zero airspeed; the wing
      // picks up the remainder as speed builds. Deliberately never a full 1 in
      // authored defs, so hovering is a controlled sink without climb input.
      this.vel.addScaledVector(this.up, gravity * flight.vtolLift * rho * dt);
      if (climb !== 0) this.vel.addScaledVector(this.up, flight.climbAuthority * climb * rho * dt);
    }
    if (this.gliding && boosting && boost) {
      // Air boost is thrust, not extra lift: it acts along the live chassis
      // nose, then the aerodynamic model turns that added speed into lift and
      // drag on subsequent substeps. The boosted ground envelope remains the
      // top-speed authority, so diving cannot stack unbounded powered speed.
      const boostedCap = h.maxSpeed * boost.speedMultiplier;
      const noseSpeed = this.vel.dot(this.bodyFwd);
      const envelope = Math.max(0, 1 - Math.max(0, noseSpeed) / boostedCap);
      this.vel.addScaledVector(
        this.bodyFwd,
        // Air-breathing like everything else: boost fades with the atmosphere,
        // so it cannot punch through the ceiling the flight thrust respects.
        h.acceleration * boost.accelerationMultiplier * (0.15 + envelope * 0.85) * this.airDensity * dt,
      );
    }
    this.tangentVel.copy(this.vel).addScaledVector(this.groundNormal, -this.vel.dot(this.groundNormal));
    this.forwardSpeed = this.tangentVel.dot(this.bodyFwd);
    const drifting = allowDrift
      && handbrake
      && this.groundedFraction > 0
      && Math.abs(this.forwardSpeed) >= h.driftMinSpeed;
    this.driftK += ((drifting ? 1 : 0) - this.driftK) * (1 - Math.exp(-12 * dt));
    // The ride-height drop snaps ~3x faster than the handling drift eases, so the
    // hull drops onto / lifts off the ground crisply the moment the slide starts/ends.
    this.driftDropK += ((drifting ? 1 : 0) - this.driftDropK) * (1 - Math.exp(-36 * dt));

    if (this.groundedFraction > 0) {
      const maxSpeed = h.maxSpeed * (boosting && boost ? boost.speedMultiplier : 1);
      let accel = throttle >= 0 ? h.acceleration : h.reverseAcceleration;
      if (boosting && boost) accel *= boost.accelerationMultiplier;
      if (throttle !== 0) {
        const cap = throttle > 0 ? maxSpeed : h.reverseSpeed;
        const along = throttle > 0 ? this.forwardSpeed : -this.forwardSpeed;
        const envelope = Math.max(0, 1 - Math.max(0, along) / cap);
        this.vel.addScaledVector(this.bodyFwd, throttle * accel * (0.15 + envelope * 0.85) * dt);
      }
      // Overdrive snap-back: boost lifts the speed cap (below), but once it ends
      // nothing else sheds the excess while the throttle is held (coast drag only
      // acts at zero throttle), so the surge would coast on top speed. Bleed the
      // forward speed above the NORMAL cap back down firmly — this is what makes
      // the boost read as a temporary overdrive that reverts, not a permanent gain.
      if (!boosting && boost && this.forwardSpeed > h.maxSpeed) {
        this.vel.addScaledVector(this.bodyFwd, -(this.forwardSpeed - h.maxSpeed) * (1 - Math.exp(-boost.overdriveDecay * dt)));
      }
      if (handbrake && !drifting) {
        const k = Math.min(1, h.brakeAcceleration * dt / Math.max(0.01, this.tangentVel.length()));
        this.vel.addScaledVector(this.tangentVel, -k);
      } else if (drifting) {
        // Handbrake sheds forward speed without cancelling the sideways
        // momentum that makes the slide readable.
        this.vel.addScaledVector(this.bodyFwd, -this.forwardSpeed * (1 - Math.exp(-h.driftDrag * dt)));
      } else if (throttle === 0) {
        this.vel.addScaledVector(this.tangentVel, -(1 - Math.exp(-h.coastDrag * dt)));
      }
      if (!handbrake && this.driftK > 0.01 && h.driftRecovery > 0) {
        // A drift release should hook up and drive out along the nose, not
        // scrub the slide away as heat. Rotate planar velocity toward the
        // chassis while retaining its magnitude; normal suspension velocity
        // remains untouched.
        const normalSpeed = this.vel.dot(this.groundNormal);
        this.recoveryVelocity.copy(this.vel).addScaledVector(this.groundNormal, -normalSpeed);
        const planarSpeed = this.recoveryVelocity.length();
        if (planarSpeed > 0.01) {
          const direction = this.forwardSpeed < 0 ? -1 : 1;
          this.recoveryVelocity.normalize().lerp(
            this.desiredFwd.copy(this.bodyFwd).multiplyScalar(direction),
            1 - Math.exp(-h.driftRecovery * dt),
          ).normalize().multiplyScalar(planarSpeed);
          this.vel.copy(this.recoveryVelocity).addScaledVector(this.groundNormal, normalSpeed);
        }
      }
      // Drift releases most lateral traction; boost can loosen it further.
      const sideSpeed = this.vel.dot(this.bodyRight);
      const driftGrip = 1 + (h.driftGripMultiplier - 1) * this.driftK;
      const grip = h.lateralGrip * (boosting ? 0.62 : 1) * driftGrip;
      this.vel.addScaledVector(this.bodyRight, -sideSpeed * (1 - Math.exp(-grip * dt)));
    }

    // Camera heading is the primary steering target; A/D biases it for keyboard-only fine control.
    this.desiredFwd.copy(this.viewHeading).applyAxisAngle(this.groundNormal, steer * 0.38);
    this.desiredFwd.addScaledVector(this.groundNormal, -this.desiredFwd.dot(this.groundNormal)).normalize();
    const cross = this.axis.crossVectors(this.bodyFwd, this.desiredFwd);
    const headingError = Math.atan2(cross.dot(this.groundNormal), this.bodyFwd.dot(this.desiredFwd));
    const yawMax = speedSteerRate(h, this.forwardSpeed) * (1 + (h.driftYawMultiplier - 1) * this.driftK);
    const targetYaw = Math.max(-yawMax, Math.min(yawMax, headingError * h.yawResponse));
    const currentYaw = this.angularVel.dot(this.groundNormal);
    this.angularVel.addScaledVector(this.groundNormal, (targetYaw - currentYaw) * (1 - Math.exp(-h.yawResponse * dt)));

    // Speeder pitch/roll follows the contact plane explicitly. Suspension
    // forces drive translation; they do not accumulate a permanent chassis
    // roll from tiny left/right probe differences.
    this.yawPart.copy(this.groundNormal).multiplyScalar(this.angularVel.dot(this.groundNormal));
    this.angularVel.lerp(this.yawPart, 1 - Math.exp(-(this.groundedFraction > 0 ? 4.5 : 0.35) * dt));
    const av = this.angularVel.length();
    if (av > 4) this.angularVel.multiplyScalar(4 / av);

    this.from.copy(this.pos);
    this.pos.addScaledVector(this.vel, dt);
    const sweep = ground.sweep?.(this.from, this.pos, this.def.collider.radius) ?? null;
    if (sweep) {
      this.pos.lerpVectors(this.from, this.pos, Math.max(0, sweep.t - 0.01));
      const vn = this.vel.dot(sweep.normal);
      this.recordImpact(-vn, this.pos, sweep.normal);
      if (vn < 0) this.vel.addScaledVector(sweep.normal, -vn * 1.08);
    }
    this.obstacleCorrection.copy(this.pos);
    if (ground.resolveObstacle?.(this.pos, this.def.collider.radius, this.obstacleCorrection)) {
      this.obstacleNormal.copy(this.obstacleCorrection).sub(this.pos);
      this.pos.copy(this.obstacleCorrection);
      if (this.obstacleNormal.lengthSq() > 1e-8) {
        this.obstacleNormal.normalize();
        const vn = this.vel.dot(this.obstacleNormal);
        this.recordImpact(-vn, this.pos, this.obstacleNormal);
        if (vn < 0) this.vel.addScaledVector(this.obstacleNormal, -vn * 1.08);
      }
    }
    const angle = this.angularVel.length() * dt;
    if (angle > 1e-6) {
      this.axis.copy(this.angularVel).normalize();
      this.quat.premultiply(this.dq.setFromAxisAngle(this.axis, angle)).normalize();
    }
    const airborneK = 1 - this.groundedFraction;
    if (airborneK > 0.001) {
      // Camera-directed 3D air control. Construct a roll-free frame around
      // the full aim ray, then ease toward it; angular momentum still carries
      // through, but the player can deliberately nose/yaw onto a landing.
      this.airRight.crossVectors(this.aimHeading, this.up).normalize();
      if (this.airRight.lengthSq() > 0.5) {
        this.airUp.crossVectors(this.airRight, this.aimHeading).normalize();
        if (this.gliding && steer !== 0) {
          const bank = -steer * this.def.glide.maxBankDeg * Math.PI / 180;
          this.airRight.applyAxisAngle(this.aimHeading, bank);
          this.airUp.applyAxisAngle(this.aimHeading, bank);
        }
        this.airMatrix.makeBasis(
          this.airRight,
          this.airUp,
          this.desiredFwd.copy(this.aimHeading).negate(),
        );
        this.airQuat.setFromRotationMatrix(this.airMatrix);
        const controlAuthority = this.gliding
          ? Math.max(0.18, Math.min(1, this.airspeed / this.def.glide.minControlSpeed)) * (this.stalled ? 0.38 : 1)
          : 1;
        this.quat.slerp(this.airQuat, 1 - Math.exp(-h.airControl * airborneK * controlAuthority * dt)).normalize();
      }
    }
    if (this.groundedFraction > 0) {
      this.alignedFwd.set(0, 0, -1).applyQuaternion(this.quat);
      this.alignedFwd.addScaledVector(this.groundNormal, -this.alignedFwd.dot(this.groundNormal)).normalize();
      if (this.alignedFwd.lengthSq() > 0.5) {
        this.alignedRight.crossVectors(this.alignedFwd, this.groundNormal).normalize();
        this.alignmentMatrix.makeBasis(
          this.alignedRight,
          this.groundNormal,
          this.desiredFwd.copy(this.alignedFwd).negate(),
        );
        this.alignmentQuat.setFromRotationMatrix(this.alignmentMatrix);
        this.quat.slerp(this.alignmentQuat, 1 - Math.exp(-h.uprightResponse * dt)).normalize();
      }
    }
    // Resolve against the FINAL orientation for this substep. Air control can
    // pitch the nose substantially during a touchdown; correcting before that
    // rotation would allow the newly rotated front footprint below terrain.
    this.resolveTerrainPenetration(ground);
    this.up.copy(this.pos).sub(ground.center).normalize();
  }

  private detachFromGround(): void {
    this.gliding = true;
    this.glideTime = 0;
    this.driftK = 0;
    this.driftDropK = 0;
    // Detachment should work from a standstill as well as off a ramp. This is
    // only enough impulse to clear the live probes; sustained height must come
    // from exchanging forward speed for aerodynamic lift.
    this.pos.addScaledVector(this.up, 0.08);
    this.vel.addScaledVector(this.up, this.def.glide.detachImpulse);
  }

  /** Apply a compact finite-wing model: q = 1/2 rho V^2, a linear lift curve
   * through the pre-stall envelope, induced drag, and separated-flow loss past
   * the stall angle. Lift remains perpendicular to the actual flight path, so
   * banking redirects it and necessarily reduces its vertical component. */
  private applyGlideAerodynamics(dt: number): void {
    const glide = this.def.glide;
    const speed = this.vel.length();
    this.airspeed = speed;
    if (speed < 0.1) {
      this.angleOfAttack = 0;
      this.stalled = true;
      return;
    }

    this.flowDir.copy(this.vel).multiplyScalar(1 / speed);
    const forwardFlow = this.bodyFwd.dot(this.flowDir);
    const verticalFlow = this.bodyUp.dot(this.flowDir);
    const alpha = Math.atan2(-verticalFlow, forwardFlow);
    this.angleOfAttack = alpha;
    const stallAlpha = glide.stallAoADeg * Math.PI / 180;
    const zeroLiftAlpha = glide.zeroLiftAoADeg * Math.PI / 180;
    const absAlpha = Math.abs(alpha);
    this.stalled = absAlpha > stallAlpha || speed < glide.minControlSpeed;

    const maxCl = glide.liftSlope * (stallAlpha - zeroLiftAlpha);
    let cl = THREE.MathUtils.clamp(glide.liftSlope * (alpha - zeroLiftAlpha), -maxCl, maxCl);
    if (absAlpha > stallAlpha) {
      // Separated flow retains a little chaotic lift but rapidly trades the
      // rest for drag. Keep the curve continuous at the stall boundary.
      const separation = Math.min(1, (absAlpha - stallAlpha) / (Math.PI * 0.5 - stallAlpha));
      cl *= THREE.MathUtils.lerp(1, 0.18, separation);
    }

    // glide.airDensity is the SEA-LEVEL density; airDensity is the local
    // fraction of it. Altitude thins the wing exactly as it thins the engine.
    const dynamicPressure = 0.5 * glide.airDensity * this.airDensity * speed * speed;
    this.liftDir.copy(this.bodyUp).addScaledVector(this.flowDir, -this.bodyUp.dot(this.flowDir));
    if (this.liftDir.lengthSq() > 1e-6) {
      this.liftDir.normalize();
      this.vel.addScaledVector(this.liftDir, dynamicPressure * glide.wingArea * cl * dt / this.def.mass);
    }

    const stallDrag = this.stalled ? 0.65 * Math.min(1, Math.max(0, absAlpha - stallAlpha) / 0.55) : 0;
    const cd = glide.baseDragCoefficient + glide.inducedDragFactor * cl * cl + stallDrag;
    this.vel.addScaledVector(this.flowDir, -dynamicPressure * glide.wingArea * cd * dt / this.def.mass);

    // A broad vehicle hull resists flying sideways. Applying this separately
    // from longitudinal drag keeps a banked turn stable without railroading the
    // forward flight path.
    const sideSpeed = this.vel.dot(this.bodyRight);
    const sideForce = 0.5 * glide.airDensity * this.airDensity * sideSpeed * Math.abs(sideSpeed)
      * glide.wingArea * glide.sideDragCoefficient;
    this.vel.addScaledVector(this.bodyRight, -sideForce * dt / this.def.mass);
  }

  /** Suspension is a soft force and may compress too far during a fast
   * touchdown or abrupt uphill transition. This is the hard chassis floor:
   * find the deepest probe-footprint penetration, lift the hull clear, and
   * remove only velocity entering that surface. Tangential ramp momentum is
   * preserved, so this prevents clipping without gluing the vehicle down. */
  private resolveTerrainPenetration(ground: VehicleGround): void {
    let deepest = 0;
    this.terrainHitNormal.set(0, 0, 0);
    for (const probe of this.def.probes) {
      this.probeOffset.set(...probe.position).applyQuaternion(this.quat);
      this.probeWorld.copy(this.pos).add(this.probeOffset);
      if (!ground.sample(this.probeWorld, this.sampleOut)) continue;
      const clearance = this.terrainCorrection.copy(this.probeWorld).sub(this.sampleOut.point).dot(this.sampleOut.normal);
      // Probe Y is measured from chassis origin. This keeps the authored
      // collider bottom above terrain even though the suspension target is
      // intentionally much higher.
      const hullFloor = Math.max(0.08, this.def.collider.halfHeight + probe.position[1]);
      // Keep a small active safety shell above the literal collider bottom.
      // At very low/parked ride heights gravity can otherwise rebuild a tiny
      // inward velocity while still inside the old post-correction gap.
      const penetration = hullFloor + 0.025 - clearance;
      if (penetration <= deepest) continue;
      deepest = penetration;
      this.terrainHitNormal.copy(this.sampleOut.normal);
    }
    if (deepest <= 0 || this.terrainHitNormal.lengthSq() < 0.5) return;
    this.terrainHitNormal.normalize();
    this.pos.addScaledVector(this.terrainHitNormal, deepest);
    const intoSurface = this.vel.dot(this.terrainHitNormal);
    this.recordImpact(-intoSurface, this.pos, this.terrainHitNormal);
    if (intoSurface < 0) this.vel.addScaledVector(this.terrainHitNormal, -intoSurface);
  }

  private recordImpact(strength: number, point: THREE.Vector3, normal: THREE.Vector3): void {
    if (strength < PRESENTATION_IMPACT_MIN || strength <= this.pendingImpact) return;
    this.pendingImpact = strength;
    this.impactPoint.copy(point);
    this.impactNormal.copy(normal).normalize();
  }
}
